import { cn } from '@/lib/utils';
import {
    artifactFilename,
    dashboardIdOf,
    artifactKeysOf,
    isArtifactElement,
    isArtifactFile
} from '@/lib/artifacts';
import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { htmlArtifactsOf, isInlineRunElement } from '@/lib/deepIntelligenceRun';
import {
    headingOffset,
    splitContentByHeadings
} from '@/lib/deepIntelligenceAvatar';
import { MessageContext } from 'contexts/MessageContext';
import { Fragment, memo, useContext, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    type IAction,
    type ICustomElement,
    type IMessageElement,
    type IStep,
    messagesState,
    sessionIdState,
    sideViewState,
    updateMessageById,
    useChatInteract
} from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import { Translator } from '@/components/i18n';
import { Button } from '@/components/ui/button';

import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import { Messages } from '..';
import { AskActionButtons } from './AskActionButtons';
import { AskFileButton } from './AskFileButton';
import { MessageAvatar } from './Avatar';
import { MessageButtons } from './Buttons';
import { MessageContent } from './Content';
import DMArtifacts from './DMArtifacts';
import { DeepIntelligenceRunHeader } from './DeepIntelligenceRunHeader';
import DMInteractiveApps from './DMInteractiveApps';
import DMInteractiveChart from './DMInteractiveChart';
import { McpAppCard } from './McpAppCard';
import { isInlineApp } from '@/components/DMMcpApps/inlineApps';
import { type SourceInfo } from './DMSourceLink';
import Step from './Step';
import { ThinkingStepsList } from './ThinkingStepsList';
import UserMessage from './UserMessage';

interface Props {
    message: IStep;
    elements: IMessageElement[];
    actions: IAction[];
    indent: number;
    isRunning?: boolean;
    isScorable?: boolean;
    scorableRun?: IStep;
    isLastAssistantInRun?: boolean;
    compact?: boolean;
}

const Message = memo(
    ({
        message,
        elements,
        actions,
        isRunning,
        indent,
        isScorable,
        scorableRun,
        isLastAssistantInRun,
        compact = false
    }: Props) => {
        const { allowHtml, cot, latex, onError, editable } =
            useContext(MessageContext);
        const chainlitApiClient = useContext(ChainlitContext);
        const sessionId = useRecoilValue(sessionIdState);
        const layoutMaxWidth = useLayoutMaxWidth();
        const contentRef = useRef<HTMLDivElement>(null);
        const [isEditingOutput, setIsEditingOutput] = useState(false);
        const [editValue, setEditValue] = useState('');
        const [hasBeenEdited, setHasBeenEdited] = useState(false);
        const setMessages = useSetRecoilState(messagesState);
        const sideView = useRecoilValue(sideViewState);
        const isUserMessage = message.type === 'user_message';
        const isSystemMessage = message.type === 'system_message';

        // Match charts to the correct assistant message per conversation turn.
        // Backend sets chart.forId = on_message run ID (context.current_run.id).
        // Each assistant_message's parentId == its on_message run ID.
        const chartElements = useMemo(() => {
            if (isUserMessage || isSystemMessage || !sideView?.elements)
                return [];
            if (message.type !== 'assistant_message') return [];
            if (!message.parentId) return [];

            return sideView.elements.filter(
                (el): el is ICustomElement =>
                    el.type === 'custom' &&
                    el.name === 'chainlitChart' &&
                    el.forId === message.parentId
            );
        }, [
            message.parentId,
            message.type,
            sideView?.elements,
            isUserMessage,
            isSystemMessage
        ]);

        // Match MCP app elements to the correct assistant message turn.
        // Same forId pattern as chartElements — elements are keyed to the
        // parent "run" step's id (context.current_run.id / the on_message
        // run step), not the assistant message's own id.
        // True for Deep Intelligence-originated content (tagged explicitly on
        // the assistant_message step's own metadata by the consumer) — used
        // below to always show the real chart instead of the click-to-open
        // app card that normal chat's MCP apps get.
        const isDeepIntelligenceApps =
            message.metadata?.source === 'deep_intelligence';

        const appElements = useMemo(() => {
            if (isUserMessage || isSystemMessage || !sideView?.elements)
                return [];
            if (message.type !== 'assistant_message') return [];
            if (!message.parentId) return [];
            return sideView.elements
                .filter(
                    (el): el is ICustomElement =>
                        el.type === 'custom' &&
                        (el as any).name === 'mcpApp' &&
                        el.forId === message.parentId
                )
                .toSorted(
                    (a, b) =>
                        ((a as any).props?.order ?? 0) -
                        ((b as any).props?.order ?? 0)
                );
        }, [
            message.parentId,
            message.type,
            sideView?.elements,
            isUserMessage,
            isSystemMessage
        ]);

        const inlineAppElements = appElements.filter(isInlineApp);
        const cardAppElements = appElements.filter((el) => !isInlineApp(el));

        const artifactElements = useMemo(() => {
            if (message.type !== 'assistant_message' || !sideView?.elements)
                return [];
            return sideView.elements.filter(
                (el) => isArtifactElement(el) && el.forId === message.id
            );
        }, [message.id, message.type, sideView?.elements]);

        const runElements = useMemo(() => {
            if (!isDeepIntelligenceApps || !message.parentId) return [];
            return elements.filter((el) => el.forId === message.parentId);
        }, [isDeepIntelligenceApps, message.parentId, elements]);

        const runArtifacts = useMemo(
            () => htmlArtifactsOf(runElements),
            [runElements]
        );

        const messageElements = useMemo(() => {
            const inlineRunElements = runElements
                .filter(isInlineRunElement)
                .map((el) => ({ ...el, forId: message.id }));
            return inlineRunElements.length
                ? [...elements, ...inlineRunElements]
                : elements;
        }, [runElements, elements, message.id]);

        const contentElements = useMemo(() => {
            if (isEmbeddedSidebar || !artifactElements.length)
                return messageElements;
            const artifactKeys = artifactKeysOf(artifactElements);
            return messageElements.filter(
                (el) => !isArtifactFile(el, artifactKeys)
            );
        }, [messageElements, artifactElements]);

        // Extract sources from sibling LLM steps (stored in metadata by callbacks)
        const sourcesData = useMemo((): SourceInfo[] => {
            if (message.type !== 'assistant_message') return [];
            if (!scorableRun?.steps) return [];
            // Find LLM steps that have sources metadata
            for (const step of scorableRun.steps) {
                if (step.type === 'llm' && step.metadata?.sources) {
                    return step.metadata.sources as SourceInfo[];
                }
            }
            return [];
        }, [message.type, scorableRun?.steps]);

        const isForkDivider =
            isSystemMessage && message.metadata?.fork_divider === true;
        const isStep = !message.type.includes('message');
        // Only keep tool calls if Chain of Thought is tool_call
        const toolCallSkip =
            isStep && cot === 'tool_call' && message.type !== 'tool';

        const hiddenSkip = isStep && cot === 'hidden';

        const skip = toolCallSkip || hiddenSkip;

        if (skip) {
            if (!message.steps) {
                return null;
            }
            return (
                <Messages
                    messages={message.steps}
                    elements={elements}
                    actions={actions}
                    indent={indent}
                    isRunning={isRunning}
                    scorableRun={scorableRun}
                />
            );
        }

        // Render fork divider as a centered message. Tagged with data-fork-divider
        // so the scroll container can anchor the view on it when the user writes
        // their first message in the fork (see ScrollContainer autoscroll).
        if (isForkDivider) {
            return (
                <div
                    data-fork-divider="true"
                    className="flex justify-center items-center px-4 py-6"
                >
                    <div className="flex items-center gap-3 text-muted-foreground text-sm">
                        <div className="flex-1 bg-border max-w-[100px] h-px" />
                        <span className="whitespace-nowrap">
                            {message.output}
                        </span>
                        <div className="flex-1 bg-border max-w-[100px] h-px" />
                    </div>
                </div>
            );
        }

        return (
            <>
                <div
                    data-step-type={message.type}
                    className={cn(compact ? 'py-0.5' : 'py-2', 'step')}
                >
                    <div
                        className="flex flex-col"
                        style={{
                            maxWidth: indent ? '100%' : layoutMaxWidth
                        }}
                    >
                        <div
                            className={cn(
                                'flex grow',
                                compact ? 'pb-0' : 'pb-1'
                            )}
                            id={`step-${message.id}`}
                        >
                            {/* User message is displayed differently */}
                            {isUserMessage ? (
                                <div className="flex flex-col grow max-w-full">
                                    <UserMessage
                                        message={message}
                                        elements={elements}
                                    >
                                        <MessageContent
                                            elements={[]}
                                            message={message}
                                            allowHtml={allowHtml}
                                            latex={latex}
                                        />
                                    </UserMessage>
                                </div>
                            ) : (
                                <div className="flex gap-4 w-full ai-message">
                                    {!isStep || !indent ? (
                                        // <MessageAvatar
                                        //     author={
                                        //         message.metadata?.avatarName ||
                                        //         message.name
                                        //     }
                                        //     isError={message.isError}
                                        // />
                                        <></>
                                    ) : null}
                                    {/* Display the step and its children */}
                                    {isStep ? (
                                        <Step
                                            step={message}
                                            isRunning={isRunning}
                                        >
                                            {message.steps ? (
                                                <Messages
                                                    messages={message.steps.filter(
                                                        (s) =>
                                                            !s.type.includes(
                                                                'message'
                                                            )
                                                    )}
                                                    elements={elements}
                                                    actions={actions}
                                                    indent={indent + 1}
                                                    isRunning={isRunning}
                                                />
                                            ) : null}
                                            {message.metadata?.progressSteps ? (
                                                <ThinkingStepsList
                                                    steps={
                                                        message.metadata
                                                            .progressSteps
                                                    }
                                                />
                                            ) : (
                                                <MessageContent
                                                    ref={contentRef}
                                                    elements={elements}
                                                    message={message}
                                                    allowHtml={allowHtml}
                                                    latex={latex}
                                                />
                                            )}
                                            <MessageButtons
                                                message={message}
                                                actions={actions}
                                                contentRef={contentRef}
                                            />
                                        </Step>
                                    ) : (
                                        // Display an assistant message
                                        <div className="flex flex-col grow items-start gap-4 min-w-[150px]">
                                            {isDeepIntelligenceApps &&
                                                isLastAssistantInRun && (
                                                    <DeepIntelligenceRunHeader
                                                        threadId={
                                                            message.threadId
                                                        }
                                                        artifacts={
                                                            runArtifacts
                                                        }
                                                    />
                                                )}
                                            {isEditingOutput ? (
                                                <div className="flex flex-col gap-2 bg-accent p-3 rounded-md w-full">
                                                    <AutoResizeTextarea
                                                        id="edit-output-input"
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) =>
                                                            setEditValue(
                                                                e.target.value
                                                            )
                                                        }
                                                        className="bg-transparent mt-1 placeholder:font-medium text-base placeholder:text-base"
                                                        maxHeight={400}
                                                    />
                                                    <div className="flex justify-end gap-4">
                                                        <Button
                                                            variant="ghost"
                                                            onClick={() =>
                                                                setIsEditingOutput(
                                                                    false
                                                                )
                                                            }
                                                        >
                                                            <Translator path="common.actions.cancel" />
                                                        </Button>
                                                        <Button
                                                            onClick={async () => {
                                                                try {
                                                                    await chainlitApiClient.updateStepOutput(
                                                                        message.id,
                                                                        editValue,
                                                                        sessionId
                                                                    );

                                                                    setMessages(
                                                                        (
                                                                            prev
                                                                        ) =>
                                                                            updateMessageById(
                                                                                prev,
                                                                                message.id,
                                                                                {
                                                                                    ...message,
                                                                                    output: editValue
                                                                                }
                                                                            )
                                                                    );

                                                                    setIsEditingOutput(
                                                                        false
                                                                    );
                                                                    setHasBeenEdited(
                                                                        true
                                                                    );

                                                                    toast.success(
                                                                        'Output updated successfully'
                                                                    );
                                                                } catch (error: any) {
                                                                    toast.error(
                                                                        'Failed to update output'
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Translator path="common.actions.confirm" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : isDeepIntelligenceApps &&
                                              appElements.length > 0 &&
                                              isLastAssistantInRun ? (
                                                <div ref={contentRef} className="w-full">
                                                    {(() => {
                                                        const sections =
                                                            splitContentByHeadings(
                                                                message.output ||
                                                                    ''
                                                            );
                                                        const offset =
                                                            headingOffset(
                                                                sections
                                                            );
                                                        return sections.map(
                                                            (section, i) => {
                                                                const app =
                                                                    appElements[
                                                                        i -
                                                                            offset
                                                                    ];
                                                                return (
                                                                    <Fragment
                                                                        key={i}
                                                                    >
                                                                        <MessageContent
                                                                            elements={
                                                                                i ===
                                                                                0
                                                                                    ? messageElements
                                                                                    : []
                                                                            }
                                                                            message={{
                                                                                ...message,
                                                                                output:
                                                                                    section
                                                                            }}
                                                                            allowHtml={
                                                                                allowHtml
                                                                            }
                                                                            latex={
                                                                                latex
                                                                            }
                                                                        />
                                                                        {app && (
                                                                            <div className="w-full">
                                                                                <McpAppCard
                                                                                    key={
                                                                                        app.id
                                                                                    }
                                                                                    className="my-8"
                                                                                    mcpAppMetadata={{
                                                                                        resourceUri:
                                                                                            (
                                                                                                app as any
                                                                                            )
                                                                                                .props
                                                                                                ?.resourceUri,
                                                                                        serverUrl:
                                                                                            (
                                                                                                app as any
                                                                                            )
                                                                                                .props
                                                                                                ?.serverUrl,
                                                                                        toolName:
                                                                                            (
                                                                                                app as any
                                                                                            )
                                                                                                .props
                                                                                                ?.toolName,
                                                                                        toolResult:
                                                                                            (
                                                                                                app as any
                                                                                            )
                                                                                                .props
                                                                                                ?.toolResult,
                                                                                        csp: (
                                                                                            app as any
                                                                                        )
                                                                                            .props
                                                                                            ?.csp
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </Fragment>
                                                                );
                                                            }
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <MessageContent
                                                    ref={contentRef}
                                                    elements={contentElements}
                                                    message={message}
                                                    allowHtml={allowHtml}
                                                    latex={latex}
                                                />
                                            )}

                                            {artifactElements.length > 0 &&
                                                !isEmbeddedSidebar && (
                                                    <div className="my-2 w-full">
                                                        <DMArtifacts
                                                            artifacts={artifactElements.map(
                                                                (el) => ({
                                                                    elementId:
                                                                        el.id,
                                                                    filename:
                                                                        artifactFilename(
                                                                            el
                                                                        ),
                                                                    dashboardId:
                                                                        dashboardIdOf(
                                                                            el
                                                                        )
                                                                })
                                                            )}
                                                        />
                                                    </div>
                                                )}

                                            {/* Embedded chat (report companion / report sidebar) never
                                                renders interactive apps — the report is the
                                                visualization surface. */}
                                            {inlineAppElements.length > 0 &&
                                                isLastAssistantInRun &&
                                                !isDeepIntelligenceApps &&
                                                !isEmbeddedSidebar &&
                                                inlineAppElements.map((app) => (
                                                    <McpAppCard
                                                        key={app.id}
                                                        className="my-4"
                                                        mcpAppMetadata={{
                                                            resourceUri: (
                                                                app as any
                                                            ).props
                                                                ?.resourceUri,
                                                            serverUrl: (
                                                                app as any
                                                            ).props?.serverUrl,
                                                            toolName: (
                                                                app as any
                                                            ).props?.toolName,
                                                            toolResult: (
                                                                app as any
                                                            ).props
                                                                ?.toolResult,
                                                            csp: (app as any)
                                                                .props?.csp
                                                        }}
                                                    />
                                                ))}

                                            {cardAppElements.length > 0 &&
                                                isLastAssistantInRun &&
                                                !isDeepIntelligenceApps &&
                                                !isEmbeddedSidebar && (
                                                    <div className="my-2 w-full">
                                                        <DMInteractiveApps
                                                            apps={cardAppElements.map(
                                                                (el) => ({
                                                                    elementId:
                                                                        el.id,
                                                                    toolName: (
                                                                        el as any
                                                                    ).props
                                                                        ?.toolName as
                                                                        | string
                                                                        | undefined,
                                                                    title: (
                                                                        el as any
                                                                    ).props
                                                                        ?.title as
                                                                        | string
                                                                        | undefined
                                                                })
                                                            )}
                                                        />
                                                    </div>
                                                )}

                                            {/* Charts are likewise never rendered in the embedded chat. */}
                                            {chartElements.length > 0 &&
                                                isLastAssistantInRun &&
                                                !isEmbeddedSidebar && (
                                                    <div className="my-4 w-full">
                                                        <DMInteractiveChart
                                                            charts={chartElements.map(
                                                                (el) => ({
                                                                    elementId:
                                                                        el.id,
                                                                    title:
                                                                        (el
                                                                            .props
                                                                            ?.title as string) ||
                                                                        'Chart',
                                                                    chartType:
                                                                        el.props
                                                                            ?.type as string
                                                                })
                                                            )}
                                                        />
                                                    </div>
                                                )}

                                            <AskFileButton
                                                messageId={message.id}
                                                onError={onError}
                                            />
                                            <AskActionButtons
                                                actions={actions}
                                                messageId={message.id}
                                            />

                                            <MessageButtons
                                                message={message}
                                                actions={actions}
                                                run={
                                                    scorableRun && isScorable
                                                        ? scorableRun
                                                        : undefined
                                                }
                                                sources={
                                                    isLastAssistantInRun
                                                        ? sourcesData
                                                        : undefined
                                                }
                                                contentRef={contentRef}
                                                isEditingOutput={
                                                    isEditingOutput
                                                }
                                                hasBeenEdited={hasBeenEdited}
                                                onEditClick={
                                                    editable
                                                        ? () => {
                                                              setEditValue(
                                                                  String(
                                                                      message.output ||
                                                                          ''
                                                                  )
                                                              );
                                                              setIsEditingOutput(
                                                                  true
                                                              );
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Make sure the child assistant messages of a step are displayed at the root level. */}
                {message.steps && isStep ? (
                    <Messages
                        messages={message.steps.filter((s) =>
                            s.type.includes('message')
                        )}
                        elements={elements}
                        actions={actions}
                        indent={0}
                        isRunning={isRunning}
                        scorableRun={scorableRun}
                    />
                ) : null}
                {/* Display the child steps if the message is not a step (usually a user message). */}
                {message.steps && !isStep ? (
                    <Messages
                        messages={message.steps}
                        elements={elements}
                        actions={actions}
                        indent={indent}
                        isRunning={isRunning}
                    />
                ) : null}
            </>
        );
    }
);

export { Message };
